import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import useSeo from '@/hooks/useSeo';
import ContactForm from '@/pages/about/components/ContactForm';
import { principles, testimonials, siteInfo, stats } from '@/mocks/incidents';

const milestones = [
  { year: '2023', text: '开始按主题归档公开报道，建立可检索的条目结构。' },
  { year: '2024', text: '补充警示科普栏目，把记录延伸为可用的防护提示。' },
  { year: '2025', text: '上线地区与分类双维度筛选，新增线索提供通道。' },
];

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      window.setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [location.hash]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zhimeizhaoming.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://zhimeizhaoming.cn/about' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: `关于${siteInfo.name}`,
        description: siteInfo.description,
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'ContactPage',
        name: `${siteInfo.name} 联系方式与线索提供`,
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'Organization',
        name: siteInfo.name,
        description: siteInfo.tagline,
        email: siteInfo.email,
        foundingDate: siteInfo.since,
      },
    ],
  };

  useSeo({
    title: '关于我们｜缅北黑料内容原则与联系方式',
    description:
      '了解缅北黑料的定位、内容整理原则与联系方式。我们以公开信息为基础追踪跨境犯罪打击动态，保护当事人隐私，并提供读者线索提供通道。',
    keywords: '缅北黑料,关于我们,内容原则,线索提供,跨境犯罪资讯',
    path: '/about',
    jsonLd,
  });

  return (
    <PageShell>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            关于 {siteInfo.name}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            {siteInfo.description}
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-12">
            <div className="lg:col-span-7">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                <span className="h-1 w-6 rounded-full bg-primary-500"></span>
                站点定位
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                做一份可以回看的跨境犯罪打击记录
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700">
                <p>
                  关于跨境电信诈骗的报道并不少，但它们大多分散在不同平台，时间一长就很难还原一件事的全貌。
                  {siteInfo.name}
                  想做的，是把这些公开信息按主题、地区与时间重新编排，让读者可以沿着时间线看清楚进展。
                </p>
                <p>
                  我们不追逐未经证实的传闻，也不对个案下结论。每一条内容都标注整理来源与时间，遇到信息存在出入时，会同时保留不同来源的表述，把判断权留给读者。
                </p>
                <p>
                  除此之外，我们更在意内容能不能被用起来。因此除了记录，还专门整理了警示科普栏目，把高发话术与诱骗链路拆开来讲，让信息真正转化为识别风险的能力。
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="h-[260px] w-full overflow-hidden rounded-xl bg-background-200 md:h-[340px]">
                <img
                  src="https://readdy.ai/api/search-image?query=documentary%20editorial%20desk%20scene%20with%20stacked%20folders%2C%20a%20notebook%20and%20a%20desk%20lamp%20casting%20warm%20light%2C%20muted%20desaturated%20warm%20tones%2C%20serious%20investigative%20mood%2C%20no%20people&width=900&height=1100&seq=mianbei-about-desk-01&orientation=portrait"
                  alt="整理资料的工作台示意"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">内容原则</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这四条原则决定了我们收录什么、如何表述，以及遇到敏感信息时怎么处理。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item, index) => (
              <div key={item.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <span className="font-heading text-2xl font-bold text-background-300">0{index + 1}</span>
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-6">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">一路走来</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                站点规模不大，但一直保持更新节奏。下面是我们做过的几件小事。
              </p>
              <ol className="mt-6 space-y-5 border-l border-background-200 pl-6">
                {milestones.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-background-50 bg-primary-500"></span>
                    <span className="font-heading text-sm font-bold text-primary-700">{item.year}</span>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  </li>
                ))}
              </ol>
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {stats.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-100 p-3 text-center">
                    <p className="font-heading text-lg font-bold text-foreground-950">{item.value}</p>
                    <p className="mt-1 text-[11px] leading-tight text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">读者口碑</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                来自家属、教师与反诈志愿者的反馈，是我们继续维护这份记录的动力。
              </p>
              <div className="mt-6 space-y-4">
                {testimonials.map((item) => (
                  <figure key={item.name} className="rounded-xl border border-background-200 bg-background-100 p-5">
                    <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                    <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{item.text}</blockquote>
                    <figcaption className="mt-4 flex items-center gap-3">
                      <span className="h-10 w-10 overflow-hidden rounded-full bg-background-200">
                        <img src={item.avatar} alt={`${item.name} 头像`} className="h-full w-full object-cover object-top" />
                      </span>
                      <span className="flex flex-col leading-tight">
                        <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                        <span className="text-[11px] text-foreground-500">{item.role}</span>
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="w-full scroll-mt-28 border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                无论是提供线索、纠正内容错误，还是申请转载，都可以通过表单或邮箱联系我们。我们通常会在三个工作日内查看。
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                    <i className="ri-mail-send-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">联系邮箱</p>
                    <p className="mt-1 break-all text-sm font-medium text-foreground-900">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-alert-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">紧急情况提示</p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-800">
                      本站无法处理紧急求助。如遇人员被困等紧急情况，请第一时间通过官方渠道反映，并保留完整沟通记录。
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-file-copy-2-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">内容转载</p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-800">
                      欢迎用于反诈宣传等非商业用途，请注明来源并保留原文中的来源标注。
                    </p>
                  </div>
                </div>
              </div>
              <Link
                to="/cases"
                className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                先浏览案件通报
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}